import axios from "axios";
import { env } from "configs/EnvironmentConfig";
import { SEC_AUTH_M_TK } from "constants/AuthConstant";

const API = axios.create({
  baseURL: env.BASE_URL,
  timeout: 600000,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true", // This header bypasses the ngrok warning
  },
});

// Add request interceptor to automatically include Authorization header
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(SEC_AUTH_M_TK);
    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // SonarQube fix: Ensure Promise rejection reason is an Error instance
    if (error instanceof Error) {
      return Promise.reject(error);
    }
    return Promise.reject(new Error(error?.message || String(error) || 'Request failed'));
  }
);

// Add response interceptor to handle errors properly
API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle network errors (no response received)
    if (!error.response) {
      // Network error - connection refused, CORS, timeout, etc.
      const networkError = {
        message: error.message || 'Network Error',
        code: error.code || 'NETWORK_ERROR',
        isNetworkError: true,
      };
      
      // Log for debugging
      console.error('Network Error:', {
        message: error.message,
        code: error.code,
        config: error.config,
        baseURL: env.BASE_URL,
      });
      
      return Promise.reject(networkError);
    }

    // Handle HTTP errors (response received but with error status)
    const status = error.response.status;
    
    // Handle unauthorized errors
    if ([400, 401, 403].includes(status)) {
      // Don't show notification here, let the calling code handle it
      return Promise.reject({
        ...error,
        status,
        message: error.response?.data?.message || 'Authentication failed',
      });
    }

    // Return the error as-is for other status codes
    return Promise.reject(error);
  }
);

console.log(`App is running at: ${env.BASE_URL}`);

// Create a separate API instance for cloud storage uploads
const CloudStorageAPI = axios.create({
  baseURL: env.CLOUD_STORAGE_BASE_URL || env.BASE_URL, // Fallback to BASE_URL if not set
  timeout: 600000,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

// Add request interceptor to automatically include Authorization header for cloud storage API
CloudStorageAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(SEC_AUTH_M_TK);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // SonarQube fix: Ensure Promise rejection reason is an Error instance
    if (error instanceof Error) {
      return Promise.reject(error);
    }
    return Promise.reject(new Error(error?.message || String(error) || 'Request failed'));
  }
);

// Add response interceptor for cloud storage API
CloudStorageAPI.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle network errors (no response received)
    if (!error.response) {
      // Network error - connection refused, CORS, timeout, etc.
      const networkError = {
        message: error.message || 'Network Error',
        code: error.code || 'NETWORK_ERROR',
        isNetworkError: true,
      };
      
      // Log for debugging
      console.error('Cloud Storage Network Error:', {
        message: error.message,
        code: error.code,
        config: error.config,
        baseURL: env.CLOUD_STORAGE_BASE_URL || env.BASE_URL,
      });
      
      return Promise.reject(networkError);
    }

    // Handle HTTP errors (response received but with error status)
    const status = error.response.status;
    
    // Handle unauthorized errors
    if ([400, 401, 403].includes(status)) {
      // Don't show notification here, let the calling code handle it
      return Promise.reject({
        ...error,
        status,
        message: error.response?.data?.message || 'Authentication failed',
      });
    }

    // Return the error as-is for other status codes
    return Promise.reject(error);
  }
);

console.log(`Cloud Storage API is running at: ${env.CLOUD_STORAGE_BASE_URL || env.BASE_URL}`);

export default API;
export { CloudStorageAPI };